import { Action, Dispatch } from 'redux'
import { push } from 'connected-react-router'
import { State } from 'index'
import { db, FirebaseDocRefType, FirebaseTimestamp, FirebaseWriteBatch } from 'firebase/index'
import { format, isSameDay, subMinutes } from 'date-fns'
import { BreadType } from 'types/bread'
import { OrderType } from 'types/checkout'
import { DeliveryStock, StockType } from 'types/stock'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { parseToDate } from 'lib/helper'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { sendNewStoreMessage } from 'reducks/messages/operations'
import { publishFirstStock } from 'repos/store'
import { updatePublishFirstStockAction } from 'reducks/store/actions'
import { addNotifiedTypes } from 'reducks/bread/operations'
import { addStoreStockMessageQueue } from 'reducks/storeStockMessageQueue/operations'

const addDeliveryStocks = (
  activeDates: Date[],
  bread: BreadType,
  orders: OrderType[],
  currentStocks: StockType[],
  individualSettingDates: Date[],
  storeDeliveryBread: StoreDeliveryBreadType
) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    if (!bread.bread_ref) return
    const breadRef = bread.bread_ref
    const storeName = getState().store.name
    const storeRef = getState().auth.store_ref
    const storeStatus = getState().store.status
    const isBeforePublishingStock = getState().store.is_before_publishing_stock
    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0
    const nextStockRefs: FirebaseDocRefType[] = []
    dispatch(showLoadingAction())

    const {
      maximum_order,
      package_size,
      delivery_company,
      frozen_delivery_type,
      is_frozen_delivery,
      hours_to_deadline,
      how_to_eat,
      how_to_store,
      choices,
      options,
    } = storeDeliveryBread

    // 取り寄せの受渡可能日を追加する
    for (let i = 0; i < activeDates.length; i = (i + 1) | 0) {
      const activeDate = activeDates[i]
      const date = format(activeDate, 'yyyyMMdd')
      // 個別設定されている受渡可能日は除外
      if (individualSettingDates.some((date) => isSameDay(date, activeDate))) continue
      const deadline = FirebaseTimestamp.fromDate(subMinutes(parseToDate(date), hours_to_deadline * 60))
      // 受渡解禁時刻ごとにstockを追加する
      const stockValue: DeliveryStock = {
        bread_ref: breadRef,
        bread_name: bread.name,
        date: date,
        choices,
        deadline: deadline,
        delivery_company,
        frozen_delivery_type,
        how_to_eat,
        how_to_store,
        image_url: bread.image_urls[0] || '',
        is_deleted: false,
        is_frozen_delivery,
        last_notified_time: null,
        maximum_order: 1,
        note: '',
        options,
        package_size,
        pickupable_time: '00:00',
        price: bread.price,
        stock: maximum_order,
        store_name: storeName,
        store_ref: storeRef,
        type: 'delivery',
      }
      const ref = storeRef.collection('store_stocks').doc(`stock_delivery_${storeDeliveryBread.ref?.id}-${date}_00:00`)
      nextStockRefs.push(ref)
      batchArray[batchIndex].set(ref, stockValue, { merge: true })
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    // 削除・上書き対象かの判定
    for (let i = 0; i < currentStocks.length; i = (i + 1) | 0) {
      const currentStock = currentStocks[i]
      if (!currentStock.stock_ref) continue
      // 更新後のstocksに存在しているので削除しない
      if (nextStockRefs.some((nextStockRef) => currentStock.stock_ref?.id === nextStockRef.id)) continue
      // 注文がある日付であれば在庫数を0に変更
      if (orders.some((order) => currentStock.date === order.date)) {
        const ref = currentStock.stock_ref
        delete currentStock.stock_ref
        batchArray[batchIndex].set(ref, { stock: 0 }, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
        continue
      }
      // 個別設定されている受渡可能日は削除しない
      if (individualSettingDates.some((date) => isSameDay(date, parseToDate(currentStock.date)))) continue
      // 在庫データを削除
      batchArray[batchIndex].delete(currentStock.stock_ref)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }
    // 店舗が初めて在庫を公開する場合、5km圏内のユーザーにPush通知を送る
    if (isBeforePublishingStock && storeStatus === 'public') {
      await publishFirstStock(storeRef)
      dispatch(updatePublishFirstStockAction())
      await sendNewStoreMessage(storeRef)
    } else {
      // この商品で初めて在庫を設定する場合、ユーザーに通知を送る
      const type = 'delivery'
      if (!bread.notified_types?.includes(type)) {
        await Promise.all([addNotifiedTypes({ bread, type }), addStoreStockMessageQueue({ breadRef, storeRef })])
      }
    }
    try {
      await batchArray.forEach(async (batch) => await batch.commit())
      const completeUrl = `/schedule/delivery/${bread.bread_ref?.id}/complete`
      dispatch(push(completeUrl))
      dispatch(hideLoadingAction())
    } catch (e) {
      console.error(e)
      dispatch(hideLoadingAction())
    }
  }
}

export default addDeliveryStocks
